type QueryKey = string | readonly unknown[];

interface QueryEntry<T = unknown> {
  data?: T;
  updatedAt: number;
  lastAccessedAt: number;
  promise?: Promise<T>;
  requestId?: symbol;
  dataVersion: number;
}

export interface FetchQueryOptions {
  /**
   * How long a successful response may be reused without another network request.
   */
  staleTimeMs?: number;
  /**
   * Force a network request. An already-running request for the same key is still
   * shared so React remounts and double taps cannot create duplicate calls.
   */
  force?: boolean;
  /**
   * Keep showing cached data when a refresh fails (offline/transient errors).
   */
  fallbackToStale?: boolean;
}

const DEFAULT_STALE_TIME_MS = 30_000;
const MAX_ENTRIES = 100;
const entries = new Map<string, QueryEntry>();

const serializeKey = (key: QueryKey): string =>
  typeof key === 'string' ? key : JSON.stringify(key);

const getUserScope = (): string => {
  if (typeof localStorage === 'undefined') return 'server';
  try {
    const rawUser = localStorage.getItem('user');
    if (rawUser) {
      const user = JSON.parse(rawUser);
      if (user?.id != null) return `user:${String(user.id)}`;
      if (user?.email) return `email:${String(user.email).toLowerCase()}`;
    }
  } catch {
    // A malformed auth record should not make the cache unavailable.
  }
  return localStorage.getItem('token') ? 'authenticated' : 'anonymous';
};

const scopedKey = (key: QueryKey): string => `${getUserScope()}::${serializeKey(key)}`;

const prune = () => {
  if (entries.size <= MAX_ENTRIES) return;
  const removable = [...entries.entries()]
    .filter(([, entry]) => !entry.promise)
    .sort(([, a], [, b]) => a.lastAccessedAt - b.lastAccessedAt);
  removable.slice(0, Math.max(0, entries.size - MAX_ENTRIES)).forEach(([key]) => entries.delete(key));
};

export const queryCache = {
  getQueryData<T>(key: QueryKey): T | undefined {
    const entry = entries.get(scopedKey(key)) as QueryEntry<T> | undefined;
    if (!entry || entry.data === undefined) return undefined;
    entry.lastAccessedAt = Date.now();
    return entry.data;
  },

  getUpdatedAt(key: QueryKey): number {
    return entries.get(scopedKey(key))?.updatedAt ?? 0;
  },

  isFresh(key: QueryKey, staleTimeMs: number = DEFAULT_STALE_TIME_MS): boolean {
    const entry = entries.get(scopedKey(key));
    return Boolean(entry?.data !== undefined && Date.now() - entry.updatedAt < staleTimeMs);
  },

  setQueryData<T>(
    key: QueryKey,
    value: T | ((previous: T | undefined) => T),
  ): T {
    const cacheKey = scopedKey(key);
    const previous = entries.get(cacheKey) as QueryEntry<T> | undefined;
    const data = typeof value === 'function'
      ? (value as (previous: T | undefined) => T)(previous?.data)
      : value;
    const now = Date.now();
    entries.set(cacheKey, {
      data,
      updatedAt: now,
      lastAccessedAt: now,
      promise: previous?.promise,
      requestId: previous?.requestId,
      dataVersion: (previous?.dataVersion ?? 0) + 1,
    });
    prune();
    return data;
  },

  async fetchQuery<T>(
    key: QueryKey,
    queryFn: () => Promise<T>,
    options: FetchQueryOptions = {},
  ): Promise<T> {
    const cacheKey = scopedKey(key);
    const staleTimeMs = options.staleTimeMs ?? DEFAULT_STALE_TIME_MS;
    const existing = entries.get(cacheKey) as QueryEntry<T> | undefined;
    const now = Date.now();

    if (!options.force && existing?.data !== undefined && now - existing.updatedAt < staleTimeMs) {
      existing.lastAccessedAt = now;
      return existing.data;
    }
    if (existing?.promise) {
      return existing.promise;
    }

    const requestId = Symbol(cacheKey);
    const dataVersionAtRequestStart = existing?.dataVersion ?? 0;
    const promise = queryFn()
      .then((data) => {
        const latest = entries.get(cacheKey) as QueryEntry<T> | undefined;
        // A socket update or optimistic mutation may have written newer data
        // while this request was in flight. Never let the older response erase it.
        if (!latest || latest.requestId !== requestId) {
          return data;
        }
        if (latest.dataVersion !== dataVersionAtRequestStart) {
          latest.promise = undefined;
          latest.requestId = undefined;
          return data;
        }
        const completedAt = Date.now();
        entries.set(cacheKey, {
          data,
          updatedAt: completedAt,
          lastAccessedAt: completedAt,
          dataVersion: dataVersionAtRequestStart + 1,
        });
        prune();
        return data;
      })
      .catch((error) => {
        const latest = entries.get(cacheKey) as QueryEntry<T> | undefined;
        if (latest?.requestId === requestId) {
          latest.promise = undefined;
          latest.requestId = undefined;
        }
        if (options.fallbackToStale !== false && existing?.data !== undefined) {
          return existing.data;
        }
        throw error;
      });

    entries.set(cacheKey, {
      data: existing?.data,
      updatedAt: existing?.updatedAt ?? 0,
      lastAccessedAt: now,
      promise,
      requestId,
      dataVersion: dataVersionAtRequestStart,
    });
    return promise;
  },

  invalidateQueries(prefix: QueryKey): void {
    const scopedPrefix = scopedKey(prefix);
    entries.forEach((entry, key) => {
      if (key.startsWith(scopedPrefix)) entry.updatedAt = 0;
    });
  },

  removeQueries(prefix: QueryKey): void {
    const scopedPrefix = scopedKey(prefix);
    [...entries.keys()].forEach((key) => {
      if (key.startsWith(scopedPrefix)) entries.delete(key);
    });
  },

  clear(): void {
    entries.clear();
  },
};

export const queryKeys = {
  chatSessions: 'chat:sessions',
  chatTimeline: (sessionId: string) => `chat:timeline:${sessionId}`,
  chatLive: (sessionId: string) => `chat:live:${sessionId}`,
  marketingCampaign: 'marketing:active-campaign',
  subscription: 'subscription:me',
  subscriptionPlans: (platform = 'web') => `subscription:plans:${platform}`,
  contractorProfile: 'profile:contractor',
  agentJobs: (tenantId: string, userId: string, sessionId: string) =>
    `agent-jobs:${tenantId}:${userId}:${sessionId}`,
} as const;
